import { formatCurrency } from './formatCurrency';

export interface ActiveRetainerDisplayInfo {
  hoursPurchased: number;
  flatAmountPurchased?: number;
  expertName?: string;
  title?: string;
}

function formatSingleRetainerPart(
  info: ActiveRetainerDisplayInfo,
  includeExpertNames: boolean,
): string {
  const flat = info.flatAmountPurchased ?? 0;
  const hours = info.hoursPurchased;
  let suffix = '';
  if (includeExpertNames) {
    suffix = ` - ${info.expertName?.trim() || 'Unknown'}`;
  } else if (info.title?.trim()) {
    suffix = ` - ${info.title.trim()}`;
  }
  if (flat > 0 && hours > 0) {
    return `${formatCurrency(flat)} + ${hours} hrs${suffix}`;
  }
  if (flat > 0) {
    return `${formatCurrency(flat)}${suffix}`;
  }
  if (hours > 0) {
    return `${hours} hrs${suffix}`;
  }
  return `0 hrs${suffix}`;
}

export interface FormatActiveRetainersOptions {
  includeExpertNames?: boolean;
  summaryOnly?: boolean;
}

export function formatActiveRetainersDisplay(
  activeRetainers: ActiveRetainerDisplayInfo[],
  options?: FormatActiveRetainersOptions,
): string {
  const count = activeRetainers.length;
  if (options?.summaryOnly) {
    return count === 1 ? '1 active retainer' : `${count} active retainers`;
  }
  const includeNames = options?.includeExpertNames !== false;
  if (count === 0) {
    return '0 Active';
  }
  const parts = activeRetainers.map((retainer) =>
    formatSingleRetainerPart(retainer, includeNames),
  );
  const detail = formatDetailParts(parts, count);
  const label = count === 1 ? '1 Active' : `${count} Active`;
  return `${label}: ${detail} funded`;
}

function formatDetailParts(parts: string[], count: number): string {
  if (count === 1) {
    return parts[0];
  }
  if (count === 2) {
    return parts.join(' & ');
  }
  return parts.slice(0, -1).join(', ') + ' & ' + parts[parts.length - 1];
}

export function formatActiveRetainerAmount(
  balance: {
    hoursPurchased: number;
    flatAmountPurchased?: number;
  },
  retainerTitle?: string,
): string {
  const flat = balance.flatAmountPurchased ?? 0;
  const hours = balance.hoursPurchased;
  if (flat === 0 && hours === 0) {
    return '0 Active';
  }
  const info: ActiveRetainerDisplayInfo = {
    ...balance,
    expertName: '',
    title: retainerTitle?.trim() ? `${retainerTitle.trim()} funded` : undefined,
  };
  const part = formatSingleRetainerPart(info, false);
  return retainerTitle?.trim()
    ? `1 Active - ${part}`
    : `1 Active - ${part} funded`;
}
